/**
 * productEditorModel — Formularmodell für den vereinheitlichten Produkt-Editor.
 * Reine Funktionen, kein React, kein I/O. Wandelt DB-Zeilen (products,
 * product_variants) in Formularzustand um, berechnet Patches für Updates
 * und validiert die Eingaben. Grundpreis-Kompatibilität wird über
 * `computeGrundpreis` aus ./grundpreis geprüft.
 */

import { computeGrundpreis } from './grundpreis';

/** '' / null / undefined → null; sonst Dezimalzahl (unterstützt "1.234,50" und "12,5") */
export function parseDecimal(v) {
  if (v === '' || v === null || v === undefined) return null;
  if (typeof v === 'number') return v;
  let s = String(v).trim();
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  return parseFloat(s);
}

/** '' / null / undefined → null; sonst Ganzzahl (kann NaN liefern) */
export function parseIntOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  return parseInt(v, 10);
}

/** null/undefined → ''; sonst String(v) */
export function toStr(v) {
  return v === null || v === undefined ? '' : String(v);
}

export const PRODUCT_DEFAULTS = {
  name: '', sku: '', category: '', unit: 'Stück', min_order_quantity: '1',
  description: '', image_url: '', is_active: true, preis: '', lagerort: '',
  stock: '0', min_stock: '10', melde_bestand: '20', hoechst_bestand: '100',
  stock_status: 'In Stock', ewk_kategorien: [], ewk_kunststoffanteil_prozent: '100',
  suppliers: [],
};

export const UNIT_OPTIONS = [
  'Stück', 'kg', 'g', 'Liter', 'ml', 'Packung', 'Karton', 'Palette',
  'Dose', 'Flasche', 'Beutel', 'Rolle', 'Sack',
];

let variantKeySeq = 0;

/** Neue, leere Variantenzeile für den Editor */
export function newVariantRow(position = 1, overrides = {}) {
  variantKeySeq += 1;
  return {
    id: null,
    _key: `new-${variantKeySeq}-${Math.random().toString(36).slice(2)}`,
    _orig: null,
    _removed: false,
    title: 'Standard',
    sku: '',
    online_preis: '',
    compare_at_preis: '',
    b2b_preis: '',
    gewicht_kg: '',
    grundpreis_menge: '',
    grundpreis_einheit: '',
    grundpreis_referenz_menge: '',
    grundpreis_referenz_einheit: '',
    grundpreis_anzeigen: false,
    position,
    is_default: position === 1,
    shopify_variant_id: null,
    bestand: null,
    aktiv: true,
    ...overrides,
  };
}

/** DB-Variante (product_variants) → Formularzeile */
export function variantToRow(v) {
  return {
    id: v.id,
    _key: v.id,
    _orig: v,
    _removed: false,
    title: v.title || '',
    sku: v.sku || '',
    online_preis: toStr(v.online_preis),
    compare_at_preis: toStr(v.compare_at_preis),
    b2b_preis: toStr(v.b2b_preis),
    gewicht_kg: toStr(v.gewicht_kg),
    grundpreis_menge: toStr(v.grundpreis_menge),
    grundpreis_einheit: v.grundpreis_einheit || '',
    grundpreis_referenz_menge: toStr(v.grundpreis_referenz_menge),
    grundpreis_referenz_einheit: v.grundpreis_referenz_einheit || '',
    grundpreis_anzeigen: !!v.grundpreis_anzeigen,
    position: v.position ?? 1,
    is_default: !!v.is_default,
    shopify_variant_id: v.shopify_variant_id || null,
    bestand: v.bestand ?? null,
    aktiv: v.aktiv !== false,
  };
}

function normaliseSupplierRow(s) {
  return { name: '', price: '', delivery_time: '', artikelnummer: '', ...s };
}

let supplierKeySeq = 0;
/** Stabiler React-Key für Lieferantenzeilen (wird nie in die DB geschrieben) */
export function newSupplierKey() {
  supplierKeySeq += 1;
  return `sup-${supplierKeySeq}-${Math.random().toString(36).slice(2, 8)}`;
}
const stripSupplierKey = (s) => { const copy = { ...s }; delete copy._key; return copy; };
const withSupplierKey = (s) => (s._key ? s : { ...s, _key: newSupplierKey() });

/** array / JSON-String / null → normalisiertes Lieferanten-Array */
function parseSuppliers(raw) {
  let arr = [];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const parsed = JSON.parse(raw);
      arr = Array.isArray(parsed) ? parsed : [];
    } catch {
      arr = [];
    }
  }
  return arr.map(normaliseSupplierRow);
}

/** Produkt (+ Varianten) → initialer Formularzustand */
export function buildInitialForm(product, variants = []) {
  if (!product) return { product: { ...PRODUCT_DEFAULTS }, rows: [newVariantRow(1)] };
  const form = {
    name: product.name || '',
    sku: product.sku || '',
    category: product.category || '',
    unit: product.unit || 'Stück',
    min_order_quantity: toStr(product.min_order_quantity ?? 1),
    description: product.description || '',
    image_url: product.image_url || '',
    is_active: product.is_active ?? product.aktiv ?? true,
    preis: toStr(product.preis ?? product.price),
    lagerort: product.lagerort || '',
    stock: toStr(product.stock ?? 0),
    min_stock: toStr(product.min_stock ?? 10),
    melde_bestand: toStr(product.melde_bestand ?? 20),
    hoechst_bestand: toStr(product.hoechst_bestand ?? 100),
    stock_status: product.stock_status || 'In Stock',
    ewk_kategorien: Array.isArray(product.ewk_kategorien) ? [...product.ewk_kategorien] : [],
    ewk_kunststoffanteil_prozent: toStr(product.ewk_kunststoffanteil_prozent ?? 100),
    suppliers: parseSuppliers(product.suppliers).map(withSupplierKey),
  };
  const rows = (variants || [])
    .filter((v) => v.aktiv !== false)
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map(variantToRow);
  return { product: form, rows };
}

export const PROTECTED_PRODUCT_FIELDS = [
  'stock', 'reserved_stock', 'available_stock', 'is_counted', 'variants',
  'shopify_product_id', 'channel_shopify', 'description_html', 'content_synced_at',
  'initial_count_at', 'initial_count_by', 'created_date', 'id',
];

function strEquiv(a, b) {
  const na = a === null || a === undefined ? '' : String(a);
  const nb = b === null || b === undefined ? '' : String(b);
  return na === nb;
}

/** Vergleicht das DB-Produkt mit dem Formular und liefert nur geänderte Felder */
export function diffProductPatch(product, form) {
  const patch = {};
  if (!product) return patch;
  const nameTrimmed = (form.name || '').trim();
  if (!strEquiv(nameTrimmed, product.name)) patch.name = nameTrimmed;
  const skuTrimmed = (form.sku || '').trim();
  const skuValue = skuTrimmed === '' ? null : skuTrimmed;
  if (!strEquiv(skuValue, product.sku)) patch.sku = skuValue;
  if (!strEquiv(form.category, product.category)) patch.category = form.category;
  if (!strEquiv(form.unit, product.unit)) patch.unit = form.unit;
  if (!strEquiv(form.description, product.description)) patch.description = form.description;
  if (!strEquiv(form.image_url, product.image_url)) patch.image_url = form.image_url;
  if (!strEquiv(form.lagerort, product.lagerort)) patch.lagerort = form.lagerort;
  if (!strEquiv(form.stock_status, product.stock_status)) patch.stock_status = form.stock_status;
  for (const key of ['min_order_quantity', 'min_stock', 'melde_bestand', 'hoechst_bestand']) {
    const value = parseIntOrNull(form[key]);
    const dbValue = product[key] ?? null;
    if (value !== dbValue) patch[key] = value;
  }
  const preisValue = parseDecimal(form.preis);
  const dbPreis = product.preis ?? product.price ?? null;
  if (preisValue !== dbPreis) {
    patch.preis = preisValue;
    patch.price = preisValue;
  }
  const activeValue = !!form.is_active;
  const dbActive = product.is_active ?? product.aktiv ?? true;
  if (activeValue !== dbActive) {
    patch.is_active = activeValue;
    patch.aktiv = activeValue;
  }
  const kategorienForm = [...(form.ewk_kategorien || [])].sort();
  const kategorienDb = [...(Array.isArray(product.ewk_kategorien) ? product.ewk_kategorien : [])].sort();
  if (JSON.stringify(kategorienForm) !== JSON.stringify(kategorienDb)) {
    patch.ewk_kategorien = [...form.ewk_kategorien];
  }
  // leeres Feld = Standard 100 % (nie NULL schreiben)
  const anteilValue = parseDecimal(form.ewk_kunststoffanteil_prozent) ?? 100;
  const dbAnteil = product.ewk_kunststoffanteil_prozent ?? 100;
  if (anteilValue !== dbAnteil) patch.ewk_kunststoffanteil_prozent = anteilValue;
  // React-Keys (_key) nie in die DB schreiben; Vergleich ohne Keys
  const suppliersForm = (form.suppliers || []).map(normaliseSupplierRow).map(stripSupplierKey);
  const suppliersDb = parseSuppliers(product.suppliers);
  if (JSON.stringify(suppliersForm) !== JSON.stringify(suppliersDb)) patch.suppliers = suppliersForm;
  return patch;
}

/** Formular → Payload für Product.create (nie id/reserved_stock/variants) */
export function buildCreatePayload(form) {
  const preisValue = parseDecimal(form.preis);
  const isActive = !!form.is_active;
  return {
    name: (form.name || '').trim(),
    sku: (form.sku || '').trim() || null,
    category: form.category || '',
    unit: form.unit || 'Stück',
    min_order_quantity: parseIntOrNull(form.min_order_quantity) ?? 1,
    description: form.description || '',
    image_url: form.image_url || '',
    is_active: isActive,
    aktiv: isActive,
    preis: preisValue,
    price: preisValue,
    lagerort: form.lagerort || '',
    stock: parseIntOrNull(form.stock) ?? 0,
    min_stock: parseIntOrNull(form.min_stock),
    melde_bestand: parseIntOrNull(form.melde_bestand),
    hoechst_bestand: parseIntOrNull(form.hoechst_bestand),
    stock_status: form.stock_status || 'In Stock',
    ewk_kategorien: [...(form.ewk_kategorien || [])],
    ewk_kunststoffanteil_prozent: parseDecimal(form.ewk_kunststoffanteil_prozent) ?? 100,
    suppliers: (form.suppliers || []).map(normaliseSupplierRow).map(stripSupplierKey),
  };
}

export const VARIANT_EDITABLE_FIELDS = [
  'title', 'sku', 'online_preis', 'compare_at_preis', 'b2b_preis', 'gewicht_kg',
  'grundpreis_menge', 'grundpreis_einheit', 'grundpreis_referenz_menge',
  'grundpreis_referenz_einheit', 'grundpreis_anzeigen', 'position',
];

export const SHOPIFY_RELEVANT_VARIANT_FIELDS = [
  'sku', 'online_preis', 'compare_at_preis', 'gewicht_kg', 'grundpreis_menge',
  'grundpreis_einheit', 'grundpreis_referenz_menge', 'grundpreis_referenz_einheit',
  'grundpreis_anzeigen',
];

/** true, wenn der Patch mind. ein Shopify-relevantes Varianten-Feld enthält */
export function needsShopifyPush(patch) {
  const p = patch || {};
  return SHOPIFY_RELEVANT_VARIANT_FIELDS.some((f) => Object.prototype.hasOwnProperty.call(p, f));
}

/** Formularzeile → normalisierte DB-typisierte Werte der editierbaren Felder */
export function rowToVariantValues(row) {
  return {
    title: (row.title || '').trim() || 'Standard',
    sku: (row.sku || '').trim() || null,
    online_preis: parseDecimal(row.online_preis),
    compare_at_preis: parseDecimal(row.compare_at_preis),
    b2b_preis: parseDecimal(row.b2b_preis),
    gewicht_kg: parseDecimal(row.gewicht_kg),
    grundpreis_menge: parseDecimal(row.grundpreis_menge),
    grundpreis_einheit: row.grundpreis_einheit || null,
    grundpreis_referenz_menge: parseIntOrNull(row.grundpreis_referenz_menge),
    grundpreis_referenz_einheit: row.grundpreis_referenz_einheit || null,
    grundpreis_anzeigen: !!row.grundpreis_anzeigen,
    position: Number(row.position),
  };
}

function origFieldValue(orig, key) {
  if (key === 'title') return orig.title || 'Standard';
  if (key === 'grundpreis_anzeigen') return !!orig.grundpreis_anzeigen;
  if (key === 'position') return orig.position ?? 1;
  return orig[key] ?? null;
}

/** Varianten-Zeilen → { updates, creates, deactivates } */
export function diffVariantPatches(rows) {
  const updates = [];
  const creates = [];
  const deactivates = [];
  for (const row of rows || []) {
    if (row._removed) {
      if (row.id) deactivates.push({ id: row.id, row });
      continue;
    }
    if (!row.id) {
      const payload = { ...rowToVariantValues(row), is_default: !!row.is_default, aktiv: true };
      creates.push({ payload, row });
      continue;
    }
    const values = rowToVariantValues(row);
    const orig = row._orig || {};
    const patch = {};
    for (const key of VARIANT_EDITABLE_FIELDS) {
      const newValue = values[key];
      const oldValue = origFieldValue(orig, key);
      if (newValue !== oldValue) patch[key] = newValue;
    }
    if (Object.keys(patch).length > 0) updates.push({ id: row.id, patch, row });
  }
  return { updates, creates, deactivates };
}

const BESTAND_LABELS = {
  min_stock: 'Mindestbestand',
  melde_bestand: 'Meldebestand',
  hoechst_bestand: 'Höchstbestand',
  min_order_quantity: 'Mindestbestellmenge',
};

function isInvalidNonNegative(value) {
  return Number.isNaN(value) || value < 0;
}

function validateRow(row, label, errors) {
  for (const priceField of ['online_preis', 'compare_at_preis', 'b2b_preis']) {
    if (isInvalidNonNegative(parseDecimal(row[priceField]))) {
      errors.push({ feld: 'rows', meldung: `Ungültiger Preis bei „${label}“` });
    }
  }
  if (isInvalidNonNegative(parseDecimal(row.gewicht_kg))) {
    errors.push({ feld: 'rows', meldung: `Ungültiges Gewicht bei „${label}“` });
  }
  const menge = parseDecimal(row.grundpreis_menge);
  if (!row.grundpreis_einheit) {
    if (menge != null && menge > 0) {
      errors.push({ feld: 'rows', meldung: `Grundpreis-Einheit fehlt bei „${label}“` });
    }
    return;
  }
  if (!Number.isFinite(menge) || menge <= 0) {
    errors.push({ feld: 'rows', meldung: `Grundpreis-Menge fehlt bei „${label}“` });
    return;
  }
  if (!row.grundpreis_referenz_einheit) {
    errors.push({ feld: 'rows', meldung: `Grundpreis-Referenz fehlt bei „${label}“` });
    return;
  }
  const refMenge = parseIntOrNull(row.grundpreis_referenz_menge) || 1;
  const result = computeGrundpreis({
    preis: 1, menge, einheit: row.grundpreis_einheit, refMenge, refEinheit: row.grundpreis_referenz_einheit,
  });
  if (result === null) {
    errors.push({ feld: 'rows', meldung: `Grundpreis-Einheiten passen nicht zusammen bei „${label}“` });
  }
}

/** Validiert das Formular; liefert Array von { feld, meldung } (leer = gültig) */
export function validateForm(form, rows) {
  const errors = [];
  if (!form.name || !form.name.trim()) {
    errors.push({ feld: 'name', meldung: 'Produktname darf nicht leer sein' });
  }
  const preisValue = parseDecimal(form.preis);
  if (isInvalidNonNegative(preisValue)) {
    errors.push({ feld: 'preis', meldung: 'Ungültiger B2B-Standardpreis' });
  }
  const anteilValue = parseDecimal(form.ewk_kunststoffanteil_prozent);
  if (Number.isNaN(anteilValue) || anteilValue < 0 || anteilValue > 100) {
    errors.push({ feld: 'ewk_kunststoffanteil_prozent', meldung: 'Kunststoffanteil muss zwischen 0 und 100 % liegen' });
  }
  for (const key of ['min_stock', 'melde_bestand', 'hoechst_bestand', 'min_order_quantity']) {
    const value = parseIntOrNull(form[key]);
    if (value !== null && isInvalidNonNegative(value)) {
      errors.push({ feld: key, meldung: `Ungültiger Wert bei ${BESTAND_LABELS[key]}` });
    }
  }
  const activeRows = (rows || []).filter((r) => !r._removed);
  if (activeRows.length === 0) {
    errors.push({ feld: 'rows', meldung: 'Mindestens eine aktive Variante ist erforderlich' });
  }
  activeRows.forEach((row, i) => {
    const label = row.title || row.sku || `Variante ${i + 1}`;
    validateRow(row, label, errors);
  });
  const seenSkus = new Set();
  let hasDuplicateSku = false;
  for (const row of activeRows) {
    const key = (row.sku || '').trim().toLowerCase();
    if (!key) continue;
    if (seenSkus.has(key)) hasDuplicateSku = true;
    seenSkus.add(key);
  }
  if (hasDuplicateSku) {
    errors.push({ feld: 'rows', meldung: 'Varianten-SKUs müssen eindeutig sein' });
  }
  return errors;
}

function snapshotRow(row) {
  const snapshot = { id: row.id, _removed: !!row._removed };
  for (const key of VARIANT_EDITABLE_FIELDS) snapshot[key] = row[key];
  return snapshot;
}

function snapshotState(state) {
  return { product: state.product, rows: (state.rows || []).map(snapshotRow) };
}

/** true, wenn sich Produkt- oder Variantenformular gegenüber `a` geändert hat */
export function isDirty(a, b) {
  return JSON.stringify(snapshotState(a)) !== JSON.stringify(snapshotState(b));
}
